// Landing page targeting office workers and young professionals:
// "wallet for young professionals", "work wallet", "wallet with badge
// holder", "professional minimalist wallet". The front ID slot doubles as
// an office badge holder, which is the wedge for this audience.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full - nothing extended just basic.jpg";
import idPhoto from "../assets/newest-version/ID Card Holder.jpg";
import cardsPhoto from "../assets/newest-version/Credit Cards Extended.jpg";
import keysPhoto from "../assets/newest-version/Back - Keys Focus.jpg";
import trackerPhoto from "../assets/newest-version/Integrated Tracking Card.jpg";

const futura = "'Futura', sans-serif";

const PRO_FAQ = [
  {
    q: "Is The Brik a good wallet for work?",
    a: "Yes. The front slot holds your office badge facing out and scannable, the RFID-protected main compartment holds 7-8 cards, folded cash rides in the back, and your office keys or fob attach to the included keyring. A rechargeable tracking card is built in, so the whole carry is findable from your phone.",
  },
  {
    q: "Can I use the front slot for my office badge?",
    a: "Yes. The front slot is intentionally not RFID-blocking, so most tap-based office badges scan without taking the card out. Badge in at the door, back in your pocket, one motion.",
  },
  {
    q: "Does it protect my cards on the commute?",
    a: "The main card compartment blocks RFID, so your tap-to-pay cards can't be skimmed on the train or in a crowd. Only the front badge slot stays scannable, by design.",
  },
  {
    q: "Will it fit in a suit or slacks pocket?",
    a: "Yes. The Brik is machined from anodized aluminum and designed for front-pocket carry, so it stays slim and flat in slacks, a suit jacket, or a work bag.",
  },
  {
    q: "What happens if I leave it at the gym or in a rideshare?",
    a: "Open the tracking app, see the wallet's location on a map, or make it ring. The rechargeable tracking card lasts up to 6 months per charge and refills on any wireless charger. No subscription.",
  },
  {
    q: "Does it look professional?",
    a: "It's a solid block of black anodized aluminum with a machined finish. No velcro, no logos across the front, nothing that looks out of place on a conference table.",
  },
];

const WORK_SCENARIOS = [
  {
    img: idPhoto,
    alt: "Office badge in the front slot of the Brik metal wallet for professionals",
    title: "Tap into the office",
    text: "Your badge lives in the front slot, facing out and scannable. Security doors, elevators, printers: one motion, no fishing through a bag or wearing a lanyard to lunch.",
  },
  {
    img: cardsPhoto,
    alt: "Credit cards in the RFID-protected compartment of the Brik work wallet",
    title: "Covered on the commute",
    text: "7-8 cards ride in the RFID-protected main compartment, so tap-to-pay cards can't be skimmed on the train. Transit card up front if you want it quick.",
  },
  {
    img: keysPhoto,
    alt: "Office keys and fob on the integrated keyring of the Brik wallet for work",
    title: "Keys and fob attached",
    text: "Office keys, mailbox key, car fob: the included keyring keeps them on the wallet, so leaving your desk never means patting four pockets.",
  },
  {
    img: trackerPhoto,
    alt: "Tracking card inside the Brik wallet for finding it at the gym, office, or rideshare",
    title: "Gym. Uber. Airport lounge.",
    text: "The places wallets disappear. The built-in tracking card means you ring it, see it on a map, and get it back before the meeting starts.",
  },
];

// Real metal-Brik buyer reviews (full set of 20 on /metal-wallet).
const PRO_REVIEWS = [
  {
    text: "I used to do the phone-wallet-keys pocket check every morning. Now it's basically just phone and Brik.",
    label: "Verified buyer",
  },
  {
    text: "The ID window is surprisingly convenient, especially at bars and when I'm traveling.",
    label: "Verified buyer",
  },
  {
    text: "It looks clean, holds everything I need, and has become part of my everyday routine.",
    label: "Verified buyer",
  },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const ProfessionalWallet = () => {
  const faqJsonLd = makeFaqJsonLd(PRO_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Wallet for Professionals", path: "/wallet-for-professionals" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Wallet for Young Professionals & Office Workers"
        description="One metal wallet for your office badge, cards, cash, keys, and a tracking card. The Brik keeps young professionals organized from the commute to the meeting. $69.99."
        path="/wallet-for-professionals"
        keywords="wallet for young professionals, work wallet, wallet with badge holder, office badge wallet, professional minimalist wallet, commuter wallet, rfid wallet for work, first job gift"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            FOR WORK AND THE COMMUTE
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            The Wallet for Young Professionals: Badge, Cards, Keys, and a
            Tracker
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "660px",
              margin: "0 auto 26px",
            }}
          >
            The Brik is a metal wallet built for the badge-in, tap-to-pay,
            gym-after-work life. Office badge up front, 7-8 cards behind RFID,
            keys on the ring, and a rechargeable tracking card inside for the
            day it stays behind in a rideshare. $69.99.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            In stock · arrives in 5-7 days · 30-day exchange guarantee
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="The Brik black anodized aluminum wallet for young professionals, closed with keys attached"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Work scenarios */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "40px" }}>
            Built for the workday
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
              gap: "24px",
            }}
          >
            {WORK_SCENARIOS.map((s) => (
              <div key={s.title}>
                <div
                  style={{
                    background: "#989898",
                    borderRadius: "14px",
                    overflow: "hidden",
                    aspectRatio: "1 / 1",
                    marginBottom: "14px",
                  }}
                >
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    width={1024}
                    height={1024}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "17px",
                    margin: "0 0 8px",
                    color: "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "14.5px", margin: 0 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ditch the lanyard */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>Retire the lanyard</h2>
          <p style={bodyText}>
            The lanyard was fine for orientation week. Wearing your badge to
            lunch three years into the job is a choice. The Brik moves the
            badge to a front slot that scans through most readers, keeps your
            cards behind RFID for the commute, and puts your keys on the
            wallet so leaving the office is a one-pocket check.
          </p>
          <p style={bodyText}>
            And it's a solid block of machined black aluminum, so it looks
            like something you chose, not something IT issued you.
          </p>
          <p style={{ ...bodyText, margin: 0 }}>
            Comparing options first? See how it stacks up against a{" "}
            <a href="/brik-vs-ridge" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Ridge-style wallet
            </a>
            , or read how the{" "}
            <a
              href="/wallet-with-built-in-tracker"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              built-in tracker
            </a>{" "}
            works. Buying for a new grad starting their first job? It also
            makes a great{" "}
            <a
              href="/gifts-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              graduation gift
            </a>
            .
          </p>
        </div>
      </section>

      {/* Reviews */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            What carriers say
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {PRO_REVIEWS.map((r) => (
              <div
                key={r.name}
                style={{
                  background: "#f7f7f7",
                  borderRadius: "12px",
                  padding: "24px",
                  flex: "1 1 260px",
                  maxWidth: "320px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <div
                  style={{ color: "#f5b315", fontSize: "16px", letterSpacing: "2px", marginBottom: "10px" }}
                  aria-label="5 out of 5 stars"
                >
                  ★★★★★
                </div>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    lineHeight: 1.55,
                    color: "#222",
                    fontStyle: "italic",
                    margin: "0 0 14px",
                  }}
                >
                  &ldquo;{r.text}&rdquo;
                </p>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#111",
                    margin: 0,
                  }}
                >
                  {r.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Work wallet questions, answered
          </h2>
          {PRO_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "white",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            Badge in. Tap to pay. Track it anywhere.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            $69.99, in stock, arrives in 5-7 days. Pick Apple or Android at
            checkout so the tracking card matches your phone.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProfessionalWallet;
